import { useState, useEffect } from 'react';

// simple clipboard helper
const copyText = (text) => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
        navigator.clipboard.writeText(text).catch(() => { });
    }
};

// MEFS MCP Server detail page

const featureCards = [
    {
        icon: (
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
            </svg>
        ),
        title: 'Native AI Integration',
        desc: 'Deep integration with Model Context Protocol (MCP) standard',
        detail: 'Seamlessly integrate with OpenClaw, Claude Desktop and other mainstream AI clients.',
        bullets: ['Fully compliant with MCP protocol specifications', 'Standardized JSON-RPC interface'],
    },
    {
        icon: (
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
            </svg>
        ),
        title: 'Data Sovereignty',
        desc: 'Combined with EVM private key encryption, data belongs exclusively to users',
        detail: 'Completely eliminate the risk of AI-generated sensitive content leakage and maintain full control of your data.',
        bullets: ['End-to-end encrypted storage', 'Local private key management'],
    },
    {
        icon: (
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 2.5c0 2.278-3.694 4.125-8.25 4.125S3.75 11.153 3.75 8.875m16.5 2.5c0 2.278-3.694 4.125-8.25 4.125S3.75 13.653 3.75 11.375" />
            </svg>
        ),
        title: 'Unlimited Memory',
        desc: 'Provide scalable decentralized "external storage" for AI agents',
        detail: 'Break through context length limitations, achieving long-term, persistent knowledge retrieval and storage.',
        bullets: ['Unlimited Capacity', 'Permanently Accessible'],
    },
    {
        icon: (
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 7.5l3 2.25-3 2.25m4.5 0h3m-9 8.25h13.5A2.25 2.25 0 0021 18V6a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 6v12a2.25 2.25 0 002.25 2.25z" />
            </svg>
        ),
        title: 'Minimal Integration',
        desc: 'Standardized API interface (Upload/Retrieve/Space)',
        detail: "Developers don't need to understand underlying storage protocols, callable with a single command.",
        bullets: ['Deploy in 3 steps', 'Zero-configuration startup'],
    },
];

const steps = [
    { num: 1, title: 'Command Initiation', desc: 'Users send tasks to AI agents (e.g. OpenClaw)' },
    { num: 2, title: 'Automated Processing', desc: 'AI completes encoding and packaging through MEFS-MCP' },
    { num: 3, title: 'Decentralized Storage', desc: 'Data encrypted and stored on MEMO global nodes' },
    { num: 4, title: 'Instant Retrieval', desc: 'AI retrieves historical data via CID anytime' },
];

const apis = [
    {
        method: 'POST',
        methodColor: 'bg-blue-500',
        title: 'Upload',
        desc: 'Upload file to MEMO network',
        params: [{ name: 'content', type: 'Base64' }, { name: 'filename', type: 'String' }],
        returns: [{ name: 'CID', type: 'String' }],
        returnsBg: 'bg-green-50',
        returnsText: 'text-green-700',
    },
    {
        method: 'GET',
        methodColor: 'bg-green-500',
        title: 'Retrieve',
        desc: 'Retrieve file by CID',
        params: [{ name: 'cid', type: 'String' }],
        returns: [{ name: 'content', type: 'Base64' }, { name: 'metadata', type: 'Object' }],
        returnsBg: 'bg-green-50',
        returnsText: 'text-green-700',
    },
    {
        method: 'GET',
        methodColor: 'bg-green-500',
        title: 'Space',
        desc: 'Query storage space usage',
        params: [{ name: 'address', type: 'String' }],
        returns: [{ name: 'used', type: 'Number' }, { name: 'total', type: 'Number' }],
        returnsBg: 'bg-green-50',
        returnsText: 'text-green-700',
    },
];

const MCPServerPage = () => {
    const [activeStep, setActiveStep] = useState(1);
    const [copiedKey, setCopiedKey] = useState(null);

    const handleCopy = (key, value) => {
        copyText(value);
        setCopiedKey(key);
        setTimeout(() => {
            setCopiedKey((current) => (current === key ? null : current));
        }, 2000);
    };

    // When navigating to this page from Home, ensure we land at the top
    useEffect(() => {
        if (typeof window !== 'undefined') {
            window.scrollTo(0, 0);
        }
    }, []);

    // Scroll to top when arriving on this page
    useEffect(() => {
        if (typeof window !== 'undefined') {
            window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        }
    }, []);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
        }
    }, []);

    const markCopied = (key) => {
        setCopiedKey(key);
        setTimeout(() => {
            setCopiedKey((current) => (current === key ? null : current));
        }, 1500);
    };

    return (
        <div>
            {/* Hero Section */}
            <div
                className="relative overflow-hidden pt-32 sm:pt-36 md:pt-40 pb-12 sm:pb-16 md:pb-20"
                style={{
                    background: 'linear-gradient(90deg, rgb(0, 222, 161) 0%, rgb(0, 222, 161) 10%, #3fb2dd 30%, hsl(220, 97.3%, 55.9%) 100%)',
                }}
            >
                <div className="absolute top-10 left-1/4 w-[300px] h-[300px] bg-cyan-400 opacity-20 rounded-full blur-[150px]" />
                <div className="absolute bottom-0 right-1/4 w-[250px] h-[250px] bg-blue-500 opacity-15 rounded-full blur-[130px]" />
                <div className="relative z-10 w-full px-4 lg:px-0 lg:w-[70%] lg:max-w-[1440px] mx-auto">
                    <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 drop-shadow-md">
                        MEFS MCP Server
                    </h1>
                    <p className="text-lg sm:text-xl md:text-2xl text-white font-semibold drop-shadow-sm">
                        Decentralized &ldquo;Cerebral Cortex&rdquo; for AI Agents
                    </p>
                </div>
            </div>

            {/* Why Choose Section */}
            <section className="py-12 sm:py-16 md:py-20 bg-white">
                <div className="w-full px-4 lg:px-0 lg:w-[70%] lg:max-w-[1440px] mx-auto">
                    <h2 className="text-2xl sm:text-3xl font-semibold text-gray-800 text-center mb-10 sm:mb-14">
                        Why Choose MEFS MCP Server?
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 items-stretch">
                        {featureCards.map((card, idx) => (
                            <div key={idx} className="border border-gray-200 rounded-xl p-6 sm:p-8 hover:shadow-md transition flex flex-col h-full">
                                <div className="w-10 h-10 bg-blue-600 text-white rounded-lg flex items-center justify-center mb-5">
                                    {card.icon}
                                </div>
                                <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-2">{card.title}</h3>
                                <p className="text-gray-600 text-sm sm:text-base mb-3">{card.desc}</p>
                                <p className="text-gray-500 text-sm mb-5 leading-relaxed">{card.detail}</p>
                                <div className="mt-auto bg-blue-50 rounded-lg p-4 space-y-2">
                                    {card.bullets.map((b, i) => (
                                        <div key={i} className="flex items-center gap-2 text-blue-700 text-sm">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-blue-500 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                                            </svg>
                                            {b}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* How It Works Section */}
            <section className="py-12 sm:py-16 md:py-20 bg-gray-50">
                <div className="w-full px-4 lg:px-0 lg:w-[70%] lg:max-w-[1440px] mx-auto">
                    <h2 className="text-2xl sm:text-3xl font-semibold text-gray-800 text-center mb-10 sm:mb-14">
                        How It Works
                    </h2>
                    <div className="bg-white rounded-2xl border border-gray-200 p-8 sm:p-10">
                        <div className="flex flex-col sm:flex-row items-start sm:items-start justify-between gap-2 sm:gap-0">
                            {steps.map((step, idx) => (
                                <>
                                    <div key={idx} className="flex sm:flex-col items-center sm:items-center gap-4 sm:gap-0 flex-1 min-w-0">
                                        <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center text-lg font-bold flex-shrink-0 sm:mb-4">
                                            {step.num}
                                        </div>
                                        <div className="sm:text-center">
                                            <p className="font-semibold text-gray-800 text-sm sm:text-base">{step.title}</p>
                                            <p className="text-gray-500 text-xs sm:text-sm mt-1 leading-relaxed">{step.desc}</p>
                                        </div>
                                    </div>
                                    {idx < steps.length - 1 && (
                                        <div className="hidden sm:flex items-start justify-center text-gray-300 text-xl pt-3 px-1 flex-shrink-0">
                                            →
                                        </div>
                                    )}
                                </>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Usage Examples Section */}
            <section className="py-12 sm:py-16 md:py-20 bg-white">
                <div className="w-full px-4 lg:px-0 lg:w-[70%] lg:max-w-[1440px] mx-auto space-y-10">
                    <div>
                        <h2 className="text-2xl sm:text-3xl font-semibold text-gray-800 text-center mb-6">
                            Usage Examples
                        </h2>
                        <div className="space-y-4">
                            {/* User prompt */}
                            <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4 sm:p-5 flex gap-3">
                                <div className="mt-1 h-6 w-6 rounded-full bg-emerald-500 text-white flex items-center justify-center text-xs font-bold">
                                    U
                                </div>
                                <p className="text-sm sm:text-base text-slate-800">
                                    Please help me generate a suspense novel of about 200 words and upload it to MEFS
                                    using <span className="font-mono">mefs-mcp-server</span>.
                                </p>
                            </div>

                            {/* Agent planning */}
                            <div className="rounded-2xl border border-slate-200 bg-sky-50/70 p-4 sm:p-5 flex gap-3">
                                <div className="mt-1 h-6 w-6 rounded-full bg-sky-500 text-white flex items-center justify-center text-xs font-bold">
                                    A
                                </div>
                                <div className="space-y-3 w-full">
                                    <p className="text-sm sm:text-base font-semibold text-slate-800">
                                        I&apos;ll create a suspense novel and upload it to MEFS. Let me break this down:
                                    </p>
                                    <div className="rounded-xl bg-white border border-slate-200 px-4 py-3 text-xs sm:text-sm text-slate-700 space-y-1">
                                        <p className="font-semibold text-emerald-600">✓ 2 of 2 To-dos Completed</p>
                                        <p>• Generate suspense story (~200 words)</p>
                                        <p>• Base64 encode and upload to MEFS</p>
                                    </div>
                                </div>
                            </div>

                            {/* Processing steps */}
                            <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 flex gap-3">
                                <div className="mt-1 h-6 w-6 rounded-full bg-sky-500 text-white flex items-center justify-center text-xs font-bold">
                                    A
                                </div>
                                <div className="space-y-2 w-full">
                                    <p className="text-sm sm:text-base font-semibold text-slate-800">
                                        Processing your request...
                                    </p>
                                    <div className="space-y-2 text-xs sm:text-sm text-slate-700">
                                        <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2">
                                            ✓ Base64 encode the novel for upload
                                        </div>
                                        <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2">
                                            ✓ Uploading to MEFS...
                                        </div>
                                        <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2">
                                            ✓ Ran Upload in <span className="font-mono">mefs-storage-server</span>
                                        </div>
                                        <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2">
                                            ✓ Cleaning up temporary files
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Result message */}
                            <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 flex gap-3">
                                <div className="mt-1 h-6 w-6 rounded-full bg-sky-500 text-white flex items-center justify-center text-xs font-bold">
                                    A
                                </div>
                                <div className="space-y-3 w-full">
                                    <p className="text-sm sm:text-base font-semibold text-slate-800">
                                        Here&apos;s what was created and uploaded:
                                    </p>
                                    <div className="rounded-xl border border-slate-100 overflow-hidden">
                                        {/* Novel summary */}
                                        <div className="bg-indigo-50 px-4 py-3 space-y-1">
                                            <p className="text-xs font-semibold text-indigo-500 uppercase tracking-wide">
                                                Suspense novel
                                            </p>
                                            <p className="font-semibold text-slate-900">The Last Door</p>
                                            <p className="mt-1 text-xs sm:text-sm text-slate-700 leading-relaxed">
                                                A short suspense story (~195 words): Emma receives an anonymous brass
                                                key with the numbers 2‑1‑4. In a dream she finds a red door labeled 214.
                                                The next day she discovers a matching door in her cellar—and finds a
                                                photo of herself from the day before. When she tries to leave, the door
                                                won&apos;t open.
                                            </p>
                                        </div>
                                        {/* Upload info */}
                                        <div className="px-4 py-3 border-t border-slate-200 text-xs sm:text-sm text-slate-700 space-y-1">
                                            <p className="font-semibold text-slate-800">Upload Info</p>
                                            <p>
                                                <span className="font-semibold">CID:</span>{' '}
                                                <span className="font-mono">
                                                    bafkreiaihvv3b6q0qca5e...
                                                </span>
                                            </p>
                                            <p>
                                                <span className="font-semibold">Filename:</span>{' '}
                                                <span className="font-mono">
                                                    The-Last-Door-suspense-novel.txt
                                                </span>
                                            </p>
                                            <p>
                                                <span className="font-semibold">Size:</span> 1,071 bytes
                                            </p>
                                        </div>
                                        {/* Tip */}
                                        <div className="px-4 py-3 border-t border-slate-200 bg-blue-50 text-xs sm:text-sm text-blue-800 flex items-start gap-2">
                                            <span className="mt-0.5 inline-flex h-4 w-4 items-center justify-center text-blue-500">
                                                💡
                                            </span>
                                            <p>
                                                You can use the retrieve tool with this CID to fetch the story from
                                                MEFS.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* First Generation visual */}
                    <div className="rounded-3xl overflow-hidden border border-slate-200 bg-gradient-to-b from-sky-50 to-sky-100">
                        <img
                            src="/mcp-first-generation.png"
                            alt="First Generation - AI content to MEFS storage flow"
                            className="w-full h-auto"
                        />
                    </div>
                </div>
            </section>

            {/* Developer Quickstart Section */}
            <section className="py-12 sm:py-16 md:py-20 bg-white">
                <div className="w-full px-4 lg:px-0 lg:w-[70%] lg:max-w-[1440px] mx-auto">
                    <h2 className="text-2xl sm:text-3xl font-semibold text-gray-900 text-center mb-3">
                        Developer Quickstart: Activate AI Storage in 3 Steps
                    </h2>
                    <p className="text-center text-gray-500 text-sm sm:text-base mb-8">
                        Just a few lines of code to give your AI agent decentralized storage.
                    </p>

                    <div className="bg-white rounded-2xl overflow-hidden border border-gray-200 shadow-sm">
                        {/* Step tabs */}
                        <div className="flex text-xs sm:text-sm bg-slate-100 text-gray-600">
                            <button
                                type="button"
                                onClick={() => setActiveStep(1)}
                                className={`flex-1 text-center py-2.5 sm:py-3 font-medium transition border-b border-transparent ${
                                    activeStep === 1
                                        ? 'bg-white text-gray-900 border-gray-200 shadow-sm'
                                        : 'text-gray-500 hover:text-gray-800'
                                }`}
                            >
                                Step 1: Configure MCP
                            </button>
                            <button
                                type="button"
                                onClick={() => setActiveStep(2)}
                                className={`flex-1 text-center py-2.5 sm:py-3 font-medium transition border-b border-l border-transparent border-gray-200 ${
                                    activeStep === 2
                                        ? 'bg-white text-gray-900 border-gray-200 shadow-sm'
                                        : 'text-gray-500 hover:text-gray-800'
                                }`}
                            >
                                Step 2: Run SSE Server
                            </button>
                            <button
                                type="button"
                                onClick={() => setActiveStep(3)}
                                className={`flex-1 text-center py-2.5 sm:py-3 font-medium transition border-b border-l border-transparent border-gray-200 ${
                                    activeStep === 3
                                        ? 'bg-white text-gray-900 border-gray-200 shadow-sm'
                                        : 'text-gray-500 hover:text-gray-800'
                                }`}
                            >
                                Step 3: Connect Client
                            </button>
                        </div>

                        {/* Step 1 content - Quick Installation & stdio configuration */}
                        {activeStep === 1 && (
                            <div className="p-5 sm:p-7 space-y-4">
                                <div className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-blue-500 text-xs font-bold">
                                        1
                                    </span>
                                    Configure MCP Server (stdio)
                                </div>
                                <p className="text-gray-600 text-xs sm:text-sm">
                                    Standard configuration for most clients. Before using the MEFS MCP server, please ensure you have created an EVM private key.
                                </p>
                                <div className="rounded-xl bg-blue-50 px-4 py-3 text-xs sm:text-sm text-blue-900 flex items-start gap-2">
                                    <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-lg bg-white shadow-sm">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 20 20"
                                            className="w-3.5 h-3.5 text-amber-400"
                                            fill="currentColor"
                                        >
                                            <path d="M10 2a5 5 0 00-3 9.09V12a1 1 0 001 1h4a1 1 0 001-1v-.91A5 5 0 0010 2z" />
                                            <path d="M8.5 14a.75.75 0 000 1.5h3a.75.75 0 000-1.5h-3zM9 17a1 1 0 102 0H9z" />
                                        </svg>
                                    </span>
                                    <p>
                                        <span className="font-semibold">Prerequisites:</span> Use an EVM wallet such as MetaMask or OKX Wallet to create a wallet and obtain your private key before starting the MCP server.
                                    </p>
                                </div>
                                <div className="space-y-2">
                                    <p className="text-xs sm:text-sm font-semibold text-gray-800">
                                        Standard configuration (stdio)
                                    </p>
                                    <p className="text-gray-600 text-xs sm:text-sm">
                                        Start the MCP server in your client with the following configuration:
                                    </p>
                                    <div className="rounded-xl bg-[#021b3a] px-4 py-3 sm:px-5 sm:py-4 text-[11px] sm:text-xs font-mono text-sky-100 overflow-x-auto relative">
                                        <button
                                            type="button"
                                            onClick={() =>
                                                handleCopy('stdio-config', `{
  "mcpServers": {
    "mefs-storage-server": {
      "command": "npx",
      "args": ["mefs-mcp-server"],
      "env": {
        "MCP_TRANSPORT_MODE": "stdio",
        "MEFS_PRIVATE_KEY": "<YOUR-PRIVATE-KEY>",
      },
      "shell": true,
      "cwd": "./",
    },
  },
}`)
                                            }
                                            className="absolute top-2 right-2 inline-flex h-7 w-7 items-center justify-center rounded-md bg-white/10 hover:bg-white/20 text-sky-100 text-xs border border-white/20"
                                            aria-label="Copy configuration"
                                        >
                                            {copiedKey === 'stdio-config' ? '✓' : '⧉'}
                                        </button>
                                        <pre className="whitespace-pre">
{`{
  "mcpServers": {
    "mefs-storage-server": {
      "command": "npx",
      "args": ["mefs-mcp-server"],
      "env": {
        "MCP_TRANSPORT_MODE": "stdio",
        "MEFS_PRIVATE_KEY": "<YOUR-PRIVATE-KEY>",
      },
      "shell": true,
      "cwd": "./",
    },
  },
}`}
                                        </pre>
                                    </div>
                                    <div className="rounded-xl bg-amber-50 px-4 py-3 text-xs sm:text-sm text-amber-900 flex items-start gap-2">
                                        <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center text-amber-500">
                                            ▲
                                        </span>
                                        <p>
                                            <span className="font-semibold">Important:</span> Replace{' '}
                                            <span className="font-mono font-semibold bg-amber-100 px-1 rounded">
                                                YOUR-PRIVATE-KEY
                                            </span>{' '}
                                            with your EVM private key.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Step 2 content - SSE mode build & start */}
                        {activeStep === 2 && (
                            <div className="p-5 sm:p-7 space-y-4">
                                <div className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-blue-500 text-xs font-bold">
                                        2
                                    </span>
                                    Run MEFS MCP Server (SSE mode)
                                </div>
                                <p className="text-gray-600 text-xs sm:text-sm">
                                    The MEFS MCP storage server supports SSE transport mode. Use the following commands to start it in SSE mode:
                                </p>
                                <div className="space-y-2">
                                    <p className="text-xs sm:text-sm font-semibold text-gray-800">
                                        Clone Repository &amp; Build
                                    </p>
                                    <div className="rounded-xl bg-[#021b3a] px-4 py-3 sm:px-5 sm:py-4 text-xs sm:text-sm font-mono text-[#4ade80] overflow-x-auto relative">
                                        <button
                                            type="button"
                                            onClick={() =>
                                                handleCopy(
                                                    'sse-clone-build',
                                                    `git clone https://github.com/memoio/mefs-mcp-server.git && cd mefs-mcp-server
pnpm install
pnpm run build`
                                                )
                                            }
                                            className="absolute top-2 right-2 inline-flex h-7 w-7 items-center justify-center rounded-md bg-white/10 hover:bg-white/20 text-sky-100 text-xs border border-white/20"
                                            aria-label="Copy commands"
                                        >
                                            {copiedKey === 'sse-clone-build' ? '✓' : '⧉'}
                                        </button>
                                        <div>git clone https://github.com/memoio/mefs-mcp-server.git &amp;&amp; cd mefs-mcp-server</div>
                                        <div>pnpm install</div>
                                        <div>pnpm run build</div>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <p className="text-xs sm:text-sm font-semibold text-gray-800">
                                        Start with SSE mode
                                    </p>
                                    <div className="rounded-xl bg-[#021b3a] px-4 py-3 sm:px-5 sm:py-4 text-xs sm:text-sm font-mono text-[#4ade80] overflow-x-auto relative">
                                        <button
                                            type="button"
                                            onClick={() => handleCopy('sse-start', 'pnpm run start:sse')}
                                            className="absolute top-2 right-2 inline-flex h-7 w-7 items-center justify-center rounded-md bg-white/10 hover:bg-white/20 text-sky-100 text-xs border border-white/20"
                                            aria-label="Copy command"
                                        >
                                            {copiedKey === 'sse-start' ? '✓' : '⧉'}
                                        </button>
                                        <div>pnpm run start:sse</div>
                                    </div>
                                    <p className="text-gray-600 text-xs sm:text-sm">
                                        You can configure <span className="font-mono font-semibold text-gray-800">MEFS_PRIVATE_KEY</span> in a <span className="font-mono">.env</span> file, add it to your environment, or start with:
                                    </p>
                                    <div className="rounded-xl bg-[#021b3a] px-4 py-3 sm:px-5 sm:py-4 text-xs sm:text-sm font-mono text-[#4ade80] overflow-x-auto relative">
                                        <button
                                            type="button"
                                            onClick={() =>
                                                handleCopy(
                                                    'sse-start-key',
                                                    'MEFS_PRIVATE_KEY=<YOUR-PRIVATE-KEY> pnpm run start:sse'
                                                )
                                            }
                                            className="absolute top-2 right-2 inline-flex h-7 w-7 items-center justify-center rounded-md bg-white/10 hover:bg-white/20 text-sky-100 text-xs border border-white/20"
                                            aria-label="Copy command"
                                        >
                                            {copiedKey === 'sse-start-key' ? '✓' : '⧉'}
                                        </button>
                                        <div>MEFS_PRIVATE_KEY=&lt;YOUR-PRIVATE-KEY&gt; pnpm run start:sse</div>
                                    </div>
                                    <div className="rounded-xl bg-emerald-50 px-4 py-3 text-xs sm:text-sm text-emerald-800 flex items-center gap-2">
                                        <span className="inline-flex h-4 w-4 items-center justify-center text-emerald-500">
                                            ✓
                                        </span>
                                        <p>Service will start on localhost:3001</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Step 3 content - Configure MCP server (SSE mode) */}
                        {activeStep === 3 && (
                            <div className="p-5 sm:p-7 space-y-3 border-t border-gray-200">
                                <div className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-blue-500 text-xs font-bold">
                                        3
                                    </span>
                                    Configure MCP Server (SSE mode)
                                </div>
                                <p className="text-gray-600 text-xs sm:text-sm">
                                    Configure your client to connect to the SSE endpoint of the MEFS storage server:
                                </p>
                                <div className="rounded-xl bg-[#021b3a] px-4 py-3 sm:px-5 sm:py-4 text-[11px] sm:text-xs font-mono text-sky-100 overflow-x-auto relative">
                                    <button
                                        type="button"
                                        onClick={() =>
                                            handleCopy('sse-config', `{
  "mcpServers": {
    "mefs-storage-server": {
      "url": "http://localhost:3001/sse",
    },
  },
}`)
                                        }
                                        className="absolute top-2 right-2 inline-flex h-7 w-7 items-center justify-center rounded-md bg-white/10 hover:bg-white/20 text-sky-100 text-xs border border-white/20"
                                        aria-label="Copy configuration"
                                    >
                                        {copiedKey === 'sse-config' ? '✓' : '⧉'}
                                    </button>
                                    <pre className="whitespace-pre">
{`{
  "mcpServers": {
    "mefs-storage-server": {
      "url": "http://localhost:3001/sse",
    },
  },
}`}
                                    </pre>
                                </div>
                                <div className="rounded-xl bg-emerald-50 px-4 py-3 mt-2 text-xs sm:text-sm text-emerald-800 space-y-2">
                                    <p>
                                        <span className="mr-1">🎉</span>
                                        <span className="font-semibold">Done!</span> Now your AI agent can use MEMO storage:
                                    </p>
                                    <div className="rounded-lg bg-white border border-emerald-100 px-3 py-2 font-mono text-[11px] sm:text-xs text-emerald-800">
                                        &quot;Please store today&apos;s meeting notes on the MEMO network&quot;
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* API Reference Section */}
            <section className="py-12 sm:py-16 md:py-20 bg-white">
                <div className="w-full px-4 lg:px-0 lg:w-[70%] lg:max-w-[1440px] mx-auto">
                    <h2 className="text-2xl sm:text-3xl font-semibold text-gray-800 text-center mb-10 sm:mb-14">
                        API Reference
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                        {apis.map((api, idx) => (
                            <div key={idx} className="border border-gray-200 rounded-xl overflow-hidden">
                                <div className="p-6">
                                    <span className={`inline-block text-white text-xs font-bold px-3 py-1 rounded-md mb-4 ${api.methodColor}`}>
                                        {api.method}
                                    </span>
                                    <h3 className="text-lg font-semibold text-gray-800 mb-1">{api.title}</h3>
                                    <p className="text-gray-500 text-sm mb-5">{api.desc}</p>
                                    <div className="bg-gray-50 rounded-lg p-4 mb-3">
                                        <p className="text-gray-500 text-xs font-semibold mb-2">Parameters:</p>
                                        {api.params.map((p, i) => (
                                            <p key={i} className="text-gray-700 text-sm">
                                                · <span className="font-mono">{p.name}</span>{' '}
                                                <span className="text-gray-400">({p.type})</span>
                                            </p>
                                        ))}
                                    </div>
                                    <div className={`${api.returnsBg} rounded-lg p-4`}>
                                        <p className={`${api.returnsText} text-xs font-semibold mb-2`}>Returns:</p>
                                        {api.returns.map((r, i) => (
                                            <p key={i} className={`${api.returnsText} text-sm`}>
                                                + <span className="font-mono">{r.name}</span>{' '}
                                                <span className="opacity-70">({r.type})</span>
                                            </p>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default MCPServerPage;
